import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ConsentAPI from "../services/ConsentAPI";
import "./css/Consent.css";

/**
 * Component for handling consent verification
 * This component is used when users click on the verification link in their email
 */
function VerifyConsent() {
  const { verificationKey } = useParams();
  const [status, setStatus] = useState({
    isLoading: true,
    isVerified: false,
    message: "Verifying your consent...",
    error: "",
  });
  const navigate = useNavigate();
  useEffect(() => {
    const verifyConsent = async () => {
      // If no key was provided in the URL, show an error
      if (!verificationKey) {
        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error:
            "No verification key was provided. Please check your email and try again.",
        });
        return;
      }

      console.log(
        `[VerifyConsent] Attempting to verify key: ${verificationKey.substring(
          0,
          10,
        )}...`,
      );
      try {
        // Call the API to verify the consent key
        const response = await ConsentAPI.verifyConsentKey(verificationKey);

        console.log("Verification response received:", response);

        // Check if verification was successful
        // Backend returns a Map with 'verified' and 'message' keys
        if (response && response.verified === true) {
          console.log("Verification successful:", response);

          // Set user as active and verified in local storage if applicable
          if (response.userUuid) {
            try {
              localStorage.setItem("userVerified", "true");
              localStorage.setItem("verifiedUserUuid", response.userUuid);

              // Store password setup token if user needs to set password
              if (response.needsPasswordSetup && response.passwordSetupToken) {
                localStorage.setItem(
                  "passwordSetupToken",
                  response.passwordSetupToken,
                );
              }
            } catch (storageError) {
              console.warn(
                "Could not store verification status in local storage:",
                storageError,
              );
            }
          }
          setStatus({
            isLoading: false,
            isVerified: true,
            message:
              response.message ||
              "Your email address has been verified and your consent has been recorded. Thank you!",
            error: "",
          });

          // Check if this is a new user who needs to set up a password
          const isNewUser =
            response.isNewUserMember || response.needsPasswordSetup;
          console.log("Is new user and needs password setup:", isNewUser);

          // Redirect based on user type
          // setTimeout(() => {
          if (isNewUser) {
            // New user needs to set up password
            navigate("/set-password");
          } else if (
            window.location.pathname.includes("/app") ||
            window.location.pathname.includes("/dashboard")
          ) {
            // Existing user, go to dashboard
            navigate("/dashboard");
          } else {
            // Default to home
            navigate("/");
          }
          // }, 3000); // Reduced delay for better UX
        } else {
          // Verification failed - handle as error
          console.log("Verification failed:", response);

          const errorMessage =
            response && response.message
              ? response.message
              : "Verification failed. The key may be invalid, expired, or already used.";

          setStatus({
            isLoading: false,
            isVerified: false,
            message: "",
            error: errorMessage,
          });
        }
      } catch (error) {
        console.error("Error verifying consent:", error);

        let errorMessage =
          "Failed to verify your consent. The verification key may be invalid or expired.";

        // Check if the error has a response with data
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        setStatus({
          isLoading: false,
          isVerified: false,
          message: "",
          error: errorMessage,
        });
      }
    };

    verifyConsent();
  }, [verificationKey, navigate]);

  // Handle retry button click
  const handleRetry = () => {
    setStatus({
      isLoading: true,
      isVerified: false,
      message: "Verifying your consent...",
      error: "",
    });

    // Retry the verification after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // Handle go home button click
  const handleGoHome = () => {
    navigate("/");
  };

  // Handle login click
  const handleLogin = () => {
    // Navigate to login page
    navigate("/login");
  };

  return (
    <div className="verify-consent-container">
      <div className="verify-consent-card">
        <h1>Consent Verification</h1>
        {status.isLoading && (
          <div className="verify-message loading-message">
            <p>{status.message}</p>
          </div>
        )}{" "}
        {!status.isLoading && status.isVerified && (
          <div className="verify-message success-message">
            <div className="checkmark-icon">✓</div>
            <p>{status.message}</p>
            <p>You will be redirected shortly...</p>
            <div style={{ marginTop: "20px" }}>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ backgroundColor: "#4caf50" }}
              >
                Continue to KinShield
              </button>
            </div>
          </div>
        )}
        {!status.isLoading && !status.isVerified && (
          <div className="verify-message error-message">
            <div className="error-icon">✗</div>
            <p>{status.error}</p>
            <div style={{ marginTop: "20px" }}>
              <button className="action-button" onClick={handleRetry}>
                Try Again
              </button>
              <button
                className="action-button"
                onClick={handleLogin}
                style={{ marginLeft: "10px", backgroundColor: "#1565c0" }}
              >
                Login
              </button>
              <button
                className="action-button"
                onClick={handleGoHome}
                style={{ marginLeft: "10px", backgroundColor: "#555" }}
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyConsent;
